/**
 * Visual assets for the Discover experience.
 *
 * Campus photography is commissioned for Campuzon and is representative of the
 * institution's setting — it is not an official institutional asset. Institution
 * marks are Campuzon-generated initial marks, deliberately NOT reproductions of
 * any official crest or logo, because inventing an institutional identity would
 * imply a relationship that does not exist.
 */

export const discoverImages = {
  /** Editorial student portrait used by both hero compositions. */
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/e1183143-0b1a-4ece-a364-d97b09ef106f.jpg',
} as const

/** One distinct photograph per institution — never the same image twice. */
export const campusImages: Record<string, string> = {
  'inst-unilag':
    'https://cdn.magicpatterns.com/patterns/generated-images/d8719dbb-1732-45f9-8f0c-5bfd19b79ada.jpg',
  'inst-oau':
    'https://cdn.magicpatterns.com/patterns/generated-images/64d4f562-cd39-4278-ae1e-d6ca5c5d6724.jpg',
  'inst-abu':
    'https://cdn.magicpatterns.com/patterns/generated-images/3366c877-8563-4d5c-82c2-e15516ce4b37.jpg',
  'inst-cu':
    'https://cdn.magicpatterns.com/patterns/generated-images/9e237d99-03d8-4ad5-8254-7711bbf35196.jpg',
  'inst-tud':
    'https://cdn.magicpatterns.com/patterns/generated-images/0925f5a9-0890-41a3-adda-fe00b9bd1ee6.jpg',
  'inst-kth':
    'https://cdn.magicpatterns.com/patterns/generated-images/07f33bd9-0372-43bc-8cee-390c5982ef0c.jpg',
}

/**
 * Identifying mark per institution. Initials plus a stable colour, so each
 * institution is recognisable at a glance without impersonating its crest.
 */
export const universityMarks: Record<string, { initials: string; className: string }> = {
  'inst-unilag': { initials: 'UL', className: 'bg-brand-700 text-white' },
  'inst-oau': { initials: 'OA', className: 'bg-info-700 text-white' },
  'inst-abu': { initials: 'AB', className: 'bg-warn-800 text-white' },
  'inst-cu': { initials: 'CU', className: 'bg-ink text-white' },
  'inst-tud': { initials: 'TU', className: 'bg-info-800 text-white' },
  'inst-kth': { initials: 'KT', className: 'bg-danger-800 text-white' },
}

/**
 * Approximate campus locations, accurate to the campus's own locality.
 *
 * These place an institution on a map; they are not a surveyed campus boundary
 * and are not used for any distance the product asks a student to rely on.
 */
export const campusCoordinates: Record<string, { lat: number; lng: number }> = {
  'inst-unilag': { lat: 6.5158, lng: 3.3898 },
  'inst-oau': { lat: 7.5171, lng: 4.523 },
  'inst-abu': { lat: 11.15, lng: 7.65 },
  'inst-cu': { lat: 6.6718, lng: 3.1583 },
  'inst-tud': { lat: 52.0022, lng: 4.3736 },
  'inst-kth': { lat: 59.3498, lng: 18.07 },
}

/** One-line editorial description per institution, shown on the Design A row card. */
export const universityBlurbs: Record<string, string> = {
  'inst-unilag':
    'A large federal university in Lagos offering undergraduate and postgraduate programmes across engineering, sciences, medicine and business.',
  'inst-oau':
    'A federal university in Ile-Ife with a broad academic base and a long-established campus community.',
  'inst-abu':
    'One of the largest universities in West Africa, spanning engineering, agriculture, medicine and the humanities.',
  'inst-cu':
    'A private university in Ogun State known for a structured residential campus and computing and engineering programmes.',
  'inst-tud':
    'A Dutch technical university with English-taught masters programmes in engineering, energy and the built environment.',
  'inst-kth':
    'A Swedish technical university offering English-taught masters programmes in engineering and technology.',
}
